import React, { useEffect, useRef, useState } from 'react'
import styles from './KeyDropPickup.module.css'
import UserName from '../common/user/UserName'
import SearchInput from '../common/search-input/SearchInput'
import Button from '../common/button/Button'
import PlusIcon from '../svgIcons/PlusIcon'
import useWindowDimension from '../../hooks/useWindowdimension'
import MageDotsIcon from '../svgIcons/MageDotsIcon'
import Pagination from '../common/pagination/pagination'
import Toast from '../common/toast/Toast'
import CustModal from '../common/custmodal/CustModal'
import AddEvent from './addEvents/AddEvent'
import moment from 'moment'
import { parsePhoneNumber } from "awesome-phonenumber";
import { deletePropertyEvents, getPropertyEvents } from '../../redux/key-pickup-drop-events/actions/key-pickup-drop-events.action'
import { useDispatch } from 'react-redux'
import { AppDispatch, IRootState } from '../../redux/rootReducer'
import { useSelector } from 'react-redux'
import Loader from '../common/loader/Loader'
import ViewEvents from './viewEvents/ViewEvents'
import { useDebounce } from '../../utils/useDebounce'

const KeyDropPickup = () => {
    const dispatch: AppDispatch = useDispatch();
    const [toastType, setToastType] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const windowWidth = useWindowDimension();
    const [showModal, setShowModal] = useState(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalCount, setTotalCount] = useState(0);
    const searchWithDebounce = useDebounce(searchTerm, 1000, setPageNumber);
    const [showActionBox, setShowActionBox] = useState<string | null>(null);
    const [actionBoxPosition, setActionBoxPosition] = useState<'top' | 'bottom'>('bottom');
    const actionButtonRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const pageLimit = 10;
    const [selectedEvent, setSelectedEvent] = useState<typeof formattedEventList[0] | null>(null);
    const [isAPILoading, setIsAPILoading] = useState(false);
    const [viewEvent, setViewEvent] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
    const [createdEventData, setCreatedEventData] = useState<EventData | null>(null);
    const { data: PropertyEventList, loading: loadingPropertyEventList } = useSelector((state: IRootState) => state?.propertyEventReducer);

    const getPropertyEventsListData = () => {
        dispatch(getPropertyEvents({ search: searchWithDebounce, page: pageNumber, limit: pageLimit, setTotalCount }));
    }

    useEffect(() => {
        getPropertyEventsListData();
    }, [searchWithDebounce, pageNumber]);


    interface EventData {
        id: string;
        name: string;
        email: string;
        phone: string;
        accessCode: string;
        eventName: string;
        description: string;
        startDate: string;
        endDate: string;
        startTime: string;
        endTime: string;
        locker: number | null;
        locationName: string;
    }

    const formatDate = (date: string) => {
        if (!date) return '-';
        return moment(date, 'YYYY-MM-DD').format('MM-DD-YYYY');
    };

    const formatTime = (time: string) => {
        if (!time) return '-';
        return moment(time, 'HH:mm A').format('hh:mm A');
    };

    const formatPhoneNumber = (phone: string) => {
        if (!phone || phone === "-") return "-";

        try {
            const phoneNumber = parsePhoneNumber(`+${phone}`);
            if (phoneNumber && phoneNumber.valid) {
                return phoneNumber.number.international;
            }
            return phone;
        } catch (error) {
            console.warn('Phone number parsing failed:', error);
            return phone;
        }
    };

    // Get badge variant based on event name
    const getEventBadgeVariant = (eventName: string) => {
        const lowerEventName = eventName?.toLowerCase() || '';

        // Check for both pickup and drop
        if ((lowerEventName.includes('pick') || lowerEventName.includes('up')) &&
            lowerEventName.includes('drop')) {
            return 'yellow';
        }
        // Check for pickup/up
        else if (lowerEventName.includes('pick') || lowerEventName.includes('up')) {
            return 'green';
        }
        // Check for drop
        else if (lowerEventName.includes('drop')) {
            return 'red';
        }

        return 'default';
    };

    const canEditEvent = (startDate: string, startTime: string): boolean => {
        if (!startDate || !startTime) return false;

        // Parse the date (YYYY-MM-DD format from API)
        const eventDate = moment(startDate, 'YYYY-MM-DD');

        // Parse the time (12-hour format with AM/PM)
        const timeParts = moment(startTime, ['hh:mm A', 'HH:mm']);

        // Combine date and time
        const eventDateTime = eventDate.clone()
            .hour(timeParts.hours())
            .minute(timeParts.minutes())
            .second(0)
            .millisecond(0);

        // Get current time plus 1 hour
        const oneHourFromNow = moment().add(1, 'hour');

        // Event can be edited if it starts at least 1 hour from now
        return eventDateTime.isAfter(oneHourFromNow);
    };

    const formattedEventList = PropertyEventList.map((event: EventData) => ({
        id: event.id,
        locationName: event.locationName,
        name: event.name,
        email: event.email || "-",
        phone: event.phone ? formatPhoneNumber(event.phone) : "-",
        startDate: formatDate(event.startDate),
        startTime: formatTime(event.startTime),
        endDate: formatDate(event.endDate),
        endTime: formatTime(event.endTime),
        eventName: event.eventName,
        locker: event.locker || "-",
        accessCode: event.accessCode || "-",
    }));

    const ActionModalBody = () => {
        return (
            <div className={styles.keyDropFeaturesActionModal}>
                <h2 className={styles.keyDropFeaturesTitle}>{`Are you sure you want to delete?`}</h2>
                <p className={styles.keyDropFeaturesDescription}>
                    Event cannot be recovered once deleted.
                </p>
                <div className={styles.keyDropFeaturesActions}>
                    <button className={styles.keyDropFeaturesCancelButton} onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </button>
                    <button className={styles.keyDropFeaturesConfirmButton}
                        onClick={onDeleteEvent}
                    >
                        Delete
                    </button>
                </div>
            </div >
        )
    }

    const SuccessModalBody = () => {
        if (!createdEventData) return null;

        const formatDateTime = (date: string, time: string) => {
            const dateFormatted = moment(date, 'YYYY-MM-DD').format('MMM DD, YYYY');
            const timeFormatted = moment(time, 'HH:mm A').format('hh:mm A');
            return `${dateFormatted} at ${timeFormatted}`;
        };

        const getOneHourBeforeStart = (startDate: string, startTime: string) => {
            const startDateTime = moment(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm A');
            const oneHourBefore = startDateTime.subtract(1, 'hour');
            return oneHourBefore.format('MMM DD, YYYY [at] hh:mm A');
        };

        return (
            <div className={styles.successModalContainer}>
                <div className={styles.successModalHeader}>
                    <div className={styles.successIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="#10B981" />
                            <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <h2 className={styles.successTitle}>Event Created Successfully!</h2>
                </div>

                <div className={styles.successContent}>
                    <div className={styles.instructionsCard}>
                        <h3 className={styles.instructionsTitle}>Important Instructions:</h3>
                        <div className={styles.instructionItem}>
                            <div className={styles.instructionIcon}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 17L12 22L22 17" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 12L12 17L22 12" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <p className={styles.instructionText}>
                                The access code for this event is: <strong>{createdEventData.accessCode}</strong>.
                                {createdEventData.email !== "-" || createdEventData.phone !== "-"
                                    ? " If you provided a cell phone number or email address, this access code has been sent to them directly."
                                    : " Since no email or phone number was provided, please pass the access code to the appropriate party."
                                }
                            </p>
                        </div>
                        <div className={styles.instructionItem}>
                            <div className={styles.instructionIcon}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#6B7280" strokeWidth="2" />
                                    <circle cx="12" cy="16" r="1" fill="#6B7280" />
                                    <path d="M7 11V7A5 5 0 0 1 17 7V11" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <p className={styles.instructionText}>
                                Please place keys in <strong>Locker {createdEventData.locker}</strong> by <strong><span className={styles.oneHourBeforeStart}>{getOneHourBeforeStart(createdEventData.startDate, createdEventData.startTime)}</span></strong>.
                            </p>
                        </div>
                    </div>
                </div>

                <div className={styles.successActions}>
                    <button
                        className={styles.successButton}
                        onClick={() => {
                            setShowSuccessModal(false);
                            setCreatedEventData(null);
                        }}
                    >
                        Got it!
                    </button>
                </div>
            </div>
        );
    };

    const handleActionButtonClick = (index: string) => {
        const rowRef = actionButtonRefs.current[index];

        if (rowRef) {
            const rect = rowRef.getBoundingClientRect();
            const parentRect = rowRef.closest(`.${CSS.escape(styles.keyDropFeaturesTableListing)}`)?.getBoundingClientRect();

            if (parentRect) {
                const parentBottom = parentRect.bottom;

                if (rect.bottom > parentBottom - 100) {
                    setActionBoxPosition('top');
                } else {
                    setActionBoxPosition('bottom');
                }

                setShowActionBox(showActionBox !== index ? index : '');
            }
        }
    };

    const onDeleteEventSuccess = (message: string) => {
        setIsAPILoading(false);
        setToastType('success');
        setShowToast(true);
        setToastMessage(message);
        getPropertyEventsListData();
    }

    const onDeleteEventFailure = (message: string) => {
        setIsAPILoading(false);
        setToastType('error');
        setShowToast(true);
        setToastMessage(message)
    }

    const onDeleteEvent = () => {
        if (selectedEvent?.id) {
            setIsAPILoading(true);
            setShowConfirmModal(false);
            dispatch(deletePropertyEvents(selectedEvent.id, onDeleteEventSuccess, onDeleteEventFailure))
        }
    }

    return (
        <>
            {showToast && <Toast type={toastType} message={toastMessage} />}
            {(loadingPropertyEventList || isAPILoading) && <Loader />}

            <CustModal
                open={viewEvent}
                setOpen={setViewEvent}
                bodyData={
                    selectedEvent && (
                        <ViewEvents
                            setOpen={setViewEvent}
                            setShowToast={setShowToast}
                            setToastMessage={setToastMessage}
                            setToastType={setToastType}
                            propertyEventList={selectedEvent}
                            onDeleteSuccess={getPropertyEventsListData}
                        />
                    )
                }
            />

            <CustModal
                open={showModal}
                setOpen={() => {
                    setShowModal(false);
                    setSelectedEvent(null);
                    setIsEditMode(false);
                }}
                onCloseModal={() => {
                    setShowModal(false);
                    setSelectedEvent(null);
                    setIsEditMode(false);
                }}
                bodyData={
                    (
                        <AddEvent
                            setOpen={setShowModal}
                            setShowToast={setShowToast}
                            setToastMessage={setToastMessage}
                            setToastType={setToastType}
                            onAddSuccess={(eventData) => {
                                getPropertyEventsListData();
                                // Show success modal for new events only
                                if (!isEditMode && eventData) {
                                    setCreatedEventData(eventData);
                                    setShowSuccessModal(true);
                                }
                            }}
                            eventData={isEditMode ? selectedEvent : undefined}  // <-- pass data for edit
                            isEditMode={isEditMode}  // <-- tell AddEvent it's edit mode
                            onCloseModal={() => {
                                setShowModal(false);
                                setSelectedEvent(null);
                                setIsEditMode(false);
                            }}
                        />
                    )
                }
            />

            {showConfirmModal && <CustModal
                open={showConfirmModal}
                bodyData={<ActionModalBody />}
                onCloseModal={() => {
                    setShowConfirmModal(false);
                }}
            />}

            {showSuccessModal && <CustModal
                open={showSuccessModal}
                bodyData={<SuccessModalBody />}
                onCloseModal={() => {
                    setShowSuccessModal(false);
                    setCreatedEventData(null);
                }}
            />}

            <div className={styles.keyDropFeaturesPageContainer}>
                <div className={styles.keyDropFeaturesPageHeader}>
                    <div className={styles.keyDropFeaturesLeftContainer}>
                        <p className={styles.keyDropFeaturesUserGreet}>
                            Welcome, <UserName />
                        </p>
                        <h3>Key PickUp & Drop</h3>
                    </div>
                    <div className={styles.rightContainer}>
                        <SearchInput
                            value={searchTerm}
                            placeholder={"Search Event/Property Name"}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: windowWidth > 1280 ? 264 : 240 }}
                        />
                        <Button variant='green' onClick={() => setShowModal(true)} className={styles.customBtn} >
                            <PlusIcon />
                            <span>{'Create Event'}</span>
                        </Button>
                    </div>
                </div>

                <div className={styles.keyDropFeaturesTableContainer}>
                    <div className={styles.keyDropFeaturesTableListing}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Event Name</th>
                                    <th>Property Name</th>
                                    {/* <th>Phone</th>
                                    <th>Email</th> */}
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Access Code</th>
                                    <th>Event Type</th>
                                    <th>Locker</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingPropertyEventList ? <Loader /> : formattedEventList?.length > 0 ? (
                                    formattedEventList.map((item: EventData, index: number) => (
                                        <tr key={index} ref={(el) => (actionButtonRefs.current[index.toString()] = el)}>
                                            <td>{item.name}</td>
                                            <td>{item.locationName}</td>
                                            {/* <td>{item.phone}</td>
                                            <td>{item.email}</td> */}
                                            <td>
                                                <div className={styles.dateTimeColumn}>
                                                    <div className={styles.dateRow}>{item.startDate}</div>
                                                    <div className={styles.timeRow}>{item.startTime}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.dateTimeColumn}>
                                                    <div className={styles.dateRow}>{item.endDate}</div>
                                                    <div className={styles.timeRow}>{item.endTime}</div>
                                                </div>
                                            </td>
                                            <td>{item.accessCode}</td>
                                            <td>
                                                <span className={`${styles.eventBadge} ${styles[`eventBadge${getEventBadgeVariant(item.eventName).charAt(0).toUpperCase() + getEventBadgeVariant(item.eventName).slice(1)}`]}`}>
                                                    {item.eventName}
                                                </span>
                                            </td>
                                            <td className={styles.lockerColumn}>{item.locker}</td>
                                            <td>
                                                {!!showActionBox && (
                                                    <div
                                                        className={styles.keyDropFeaturesDropDownBackDrop}
                                                        onClick={() => setShowActionBox('')}
                                                    ></div>
                                                )}
                                                <div className={styles.keyDropFeaturesActionButtonWrapper}>
                                                    <button
                                                        className={styles.keyDropFeaturesMageDotsIcon}
                                                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                            event.stopPropagation();
                                                            handleActionButtonClick(index.toString());
                                                        }}
                                                    >
                                                        <MageDotsIcon />
                                                    </button>
                                                    <div
                                                        className={`${styles.keyDropFeaturesActionContainer} ${index.toString() === showActionBox ? styles.active : ''
                                                            } ${actionBoxPosition === 'top' ? styles.top : ''}`}
                                                    >
                                                        <div
                                                            className={styles.keyDropFeaturesActionBox}
                                                            role="button"
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();
                                                                // View action
                                                                setShowActionBox(null);
                                                                setSelectedEvent(PropertyEventList[index]);
                                                                setViewEvent(true);

                                                            }}
                                                        >
                                                            <span>View</span>
                                                        </div>
                                                        {/* {canEditEvent(PropertyEventList[index].startDate, PropertyEventList[index].startTime) && (
                                                            <div
                                                                className={styles.keyDropFeaturesActionBox}
                                                                role="button"
                                                                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                    event.stopPropagation();
                                                                    const selected = PropertyEventList[index];
                                                                    setSelectedEvent(selected);
                                                                    setIsEditMode(true);
                                                                    setShowActionBox(null);
                                                                    setShowModal(true);
                                                                    setShowActionBox(null);
                                                                }}
                                                            >
                                                                <span>Edit</span>
                                                            </div>
                                                        )} */}

                                                        <div
                                                            className={styles.keyDropFeaturesActionBox}
                                                            role="button"
                                                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                                event.stopPropagation();
                                                                setShowActionBox(null);
                                                                setSelectedEvent(PropertyEventList[index]);
                                                                setShowConfirmModal(true);
                                                            }}
                                                        >
                                                            <span>Delete</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className={styles.noData} colSpan={8}>
                                            No Event Data Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div>
                        <Pagination
                            className="pagination-bar"
                            currentPage={pageNumber}
                            totalCount={Math.ceil(totalCount / pageLimit)}
                            pageSize={1}
                            onPageChange={(page: number) => setPageNumber(page)}
                        />

                    </div>
                </div>
            </div>
        </>
    )
}

export default KeyDropPickup
